import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, Popover2, Fragment} from '@api'
import {Menu, MenuDivider, MenuItem} from '@blueprintjs/core'
import {AssortmentCardFilterMenuItems, assortmentIcons, AssortmentLayoutContextMenuItem, ContextMenuItem, useAssortmentCardStore, useVipLocalStore, utils, VipLocalStore} from '@modules'
import {ToggleExpandButton} from '@modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/Header'
import {icons, StoresInstance} from '@stores'
import {PdfSvgIcon} from '@stores/ui'
import {PrimaryIconButton} from 'centric-ui-components'
import * as css from './AssortmentFloatingToolbar.css'

/// region Components

interface MyProps {
  className?: string
}

export const AssortmentFloatingToolbar = observer<MyProps, HTMLDivElement>(function AssortmentFloatingToolbar({className, ...props}, ref) {
  const store = useAssortmentCardStore(),
    {isShowingHeaderDropdown, root: {canExpandCards}} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <div className={classNames(css.root, className)}
         onDoubleClick={utils.eatMouseEvent}
         data-is-showing-dropdown={isShowingHeaderDropdown}
         ref={ref}>
      {/*<ToggleShowHeader />*/}
      <FocusOnAssortment/>
      {/*<ToggleSecondLevelButton />*/}
      {/*<ToggleSwatches/>*/}
      {/*<ShowInlineAssortmentHeroCardsButton tooltipPosition='right' />*/}
      {/*<MoreInfoAssortmentCardButton/>*/}
      <ToggleExpandButton tooltipPosition='bottom'/>
      <AssortmentCardTitleDropdownMenuButton/>
    </div>
  )
}, {forwardRef: true})

export const ToggleShowHeader = observer(function ToggleShowHeader() {
  const {onToggleShowHeader, showHeader, root: {settings: {hideAllHeaders}}} = useAssortmentCardStore()

  return <PrimaryIconButton tooltip='Show Header' onClick={onToggleShowHeader} icon={assortmentIcons.headers} active={showHeader}/>
})

export const ToggleSwatches = observer(function ToggleSwatches() {
  const {onToggleFooter, showFooter, root: {settings: {hideAllFooters}}} = useAssortmentCardStore()

  return <PrimaryIconButton tooltip='Show Footer' onClick={onToggleFooter} icon={assortmentIcons.footer} active={showFooter && !hideAllFooters}/>
})

export const FocusOnAssortment = observer(function FocusOnAssortment() {
  const {onZoom, isZoomed} = useAssortmentCardStore()

  return (<PrimaryIconButton tooltip='Focus Mode' onClick={onZoom} icon={assortmentIcons.zoom} active={isZoomed}/>)
})

export const ToggleSecondLevelButton = observer(function ToggleSecondLevelButton() {
  const {onToggleSecondLevel, isShowingSecondLevel} = useAssortmentCardStore()

  return (<PrimaryIconButton tooltip='Show Second Level' onClick={onToggleSecondLevel} icon={assortmentIcons.secondLevel} active={isShowingSecondLevel}/>)
})

export const ShowInlineAssortmentHeroCardsButton = observer<{ tooltipPosition? }>(function ShowHeroCardsButton({tooltipPosition}) {
  const {strings, showInlineHeroCards, toggleShowInlineHeroCards} = useAssortmentCardStore()

  return (
    <PrimaryIconButton
      tooltip={strings.toggleExpandCollapse}
      tooltipPosition={tooltipPosition ?? 'bottom'}
      icon={icons.heroCards}
      active={showInlineHeroCards}
      onClick={toggleShowInlineHeroCards}
    />)
})
export const MoreInfoAssortmentCardButton = observer(function MoreInfoAssortmentCardButton() {
  const {strings} = useAssortmentCardStore()

  return (
    <Popover2
      interactionKind='hover'
      position='right'
      isOpen={false}
      minimal={false}
      content={
        <div>
          More info panel Contents
        </div>
      }>
      <PrimaryIconButton
        tooltip={'More Info'}
        tooltipPosition='right'
        icon={icons.info}
      />
    </Popover2>)
})
/// endregion

export const AssortmentCardTitleDropdownMenuButton = observer(function AssortmentCardTitleDropdownMenuButton() {
  const {
    stores: {
      deployments: {deploymentConfig: {settings: {whiteboard}}}
    },
    root: {canExpandCards}, catalog: {pdfCatalogStore: {isServerPdfAvailable, availableTemplates}},
    onSendToWhiteboard, onHeaderDropdownOpened, onHeaderDropdownClosed,
    expanded, toggleExpanded, assortmentCardSettings: cardSettings
  } = useAssortmentCardStore()

  return (<Popover2
    className={css.assortmentDropdownMenu}
    onOpened={onHeaderDropdownOpened}
    onClosed={onHeaderDropdownClosed}
    content={<Menu>
      {canExpandCards && <MenuItem text={expanded ? 'Collapse Group' : 'Expand Group'}
	                               icon={expanded ? icons.collapse : icons.expand}
	                               onClick={toggleExpanded}
	  />}
      {isServerPdfAvailable && availableTemplates.has('assortment') && <AssortmentCardPdfMenuItem/>}
      <AssortmentLayoutContextMenuItem/>
      {whiteboard && <MenuItem text='Send to Whiteboard' onClick={onSendToWhiteboard}/>}
      <MenuDivider/>
      <AssortmentCardFilterMenuItems/>
      <ClearAssortmentCardOverridesMenuItem/>
      <SaveAssortmentCardOverridesMenuItem/>
      {cardSettings.heroCards.showAll && <ContextMenuItem dividerBefore icon='blank' text='Hide Extra Heroes' onClick={() => cardSettings.heroCards.showAll = false}/>}
    </Menu>}
    interactionKind='hover'
    position='bottom-right'
  >
    <PrimaryIconButton wrapperClassName={css.assortmentDropdownIcon} icon='menu'/>
  </Popover2>)
})

export const ClearAssortmentCardOverridesMenuItem = observer(function ClearAssortmentCardOverridesMenuItem() {
  const {componentStores: {heroCards}} = useAssortmentCardStore()

  if (!heroCards) return null
  return (<Fragment>
    <ContextMenuItem icon='clean' dividerBefore text='Reset Orders' disabled={!heroCards.hasSortOverrides} onClick={heroCards.clearSortOverrides}/>
    <ContextMenuItem icon='clean' text='Reset Combines/Splits' disabled={!heroCards.hasCombines} onClick={heroCards.clearCombinesOverrides}/>
  </Fragment>)
})

export const SaveAssortmentCardOverridesMenuItem = observer(function SaveAssortmentCardOverridesMenuItem() {
  const {componentStores: {heroCards}, globalOverrides, deleteGlobalOverrides, isOverrideDifferentThanDefault} = useAssortmentCardStore()

  if (!heroCards?.hasOverrides) return null
  return (
    <Fragment>
      {globalOverrides && <ContextMenuItem icon='trash' dividerBefore text='Delete Customizations' onClick={deleteGlobalOverrides}/>}
      <ContextMenuItem icon='cloud-upload' disabled={!isOverrideDifferentThanDefault} dividerBefore={!globalOverrides} text='Save Customizations' onClick={heroCards.saveOverridesToServer}/>
    </Fragment>
  )
})

export const AssortmentCardPdfMenuItem = observer(function AssortmentCardPdfMenuItem() {
  const {createPDF, addToPDF} = useAssortmentCardStore()

  return (
    <ContextMenuItem icon={<PdfSvgIcon/>}
                     text='Generate PDF'
                     onClick={createPDF} shouldDismissPopover
    />)

  // return (
  //   <ContextMenuItem icon={<PdfSvgIcon/>}
  //                    text='PDF'
  //   >
  //     <ContextMenuItem icon='new-layer' text='New...' onClick={createPDF} shouldDismissPopover>
  //       <ContextMenuItem icon='document' text='Create PDF' onClick={createPDF} shouldDismissPopover/>
  //       <ContextMenuItem icon='add' text='Add to PDF' onClick={addToPDF} shouldDismissPopover/>
  //     </ContextMenuItem>
  //     <ContextMenuItem icon='folder-open' text='Open...'>
  //
  //     </ContextMenuItem>
  //   </ContextMenuItem>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/AssortmentFloatingToolbar.tsx